/**
 * /family/:tree/member/:memberId — Rich member profile ("living digital archive").
 *
 * A single scrolling page with in-page anchors. Only sections that have data
 * render — the rest are hidden. Stats and timeline are computed server-side.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, Users, Baby, Briefcase, GraduationCap, MapPin, Flag, Church,
  Languages as LangIcon, Heart, Trophy, HandHeart, Images, Video, AudioLines,
  FileText, Flower2, BookOpen, Sparkles, Clock, Phone, Mail, Globe, TreeDeciduous,
  UserPlus, Pencil, Upload, Link as LinkIcon, MessageSquare, Loader2, X,
} from "lucide-react";
import TranslatableText from "@/components/TranslatableText";
import AncestorAvatar from "@/components/AncestorAvatar";
import CopyLinkIcon from "@/components/CopyLinkIcon";
import ProfileCommunity from "@/components/ProfileCommunity";
import VoiceMemories from "@/components/memorials/VoiceMemories";
import MemorialPageButton from "@/components/memorials/MemorialPageButton";
import MemberForum from "@/components/family/MemberForum";
import ContactMemberModal from "@/components/ContactMemberModal";
import SuggestFixModal from "@/components/SuggestFixModal";
import MemberContributeModal from "@/components/family/MemberContributeModal";
import WishesWall from "@/components/family/WishesWall";

// ---------- helpers ----------
const has = (v) => {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).some((k) => has(v[k]));
  if (typeof v === "string") return v.trim().length > 0;
  return true;
};
const fmtDate = (d) => {
  if (!d) return "";
  if (d.length === 4) return d;
  const iso = d.slice(0, 10);
  const dt = new Date(iso);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};
const yr = (d) => (d && String(d).slice(0, 4)) || "";

export default function MemberProfile() {
  const { treeSlug, memberId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showSuggestFix, setShowSuggestFix] = useState(false);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        // treeSlug may be a slug or id; the endpoint accepts either via the same param.
        const { data: t } = await http.get(`/family-trees/${treeSlug}`);
        const treeId = t.tree?.id || t.id;
        const { data: p } = await http.get(`/family-trees/${treeId}/members/${memberId}/profile`);
        setData(p);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Couldn't load this profile.");
      } finally { setLoading(false); }
    })();
  }, [treeSlug, memberId]);

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-24 text-center opacity-60">Loading…</div>;
  if (err) return <div className="max-w-4xl mx-auto px-6 py-24 text-center" style={{ color: "#B23A3A" }}>{err}</div>;
  if (!data) return null;

  const m = data.member || {};
  const stats = data.stats || {};
  const tl = data.timeline || [];
  const comp = data.completeness || {};
  const name = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
  const deceased = !!m.death_date || m.current_status === "deceased";
  const displayed = deceased ? "In loving memory" : (m.current_status === "unknown" ? "Status Unknown" : "Living");
  const isUnknown = !deceased && m.current_status === "unknown";
  const updateStatus = async (next) => {
    if (savingStatus) return;
    const label = next === "deceased" ? "mark as deceased" : "mark as living";
    if (!window.confirm(`Are you sure you want to ${label}? You can always change this later.`)) return;
    setSavingStatus(true);
    try {
      const treeId = data.tree?.id || treeSlug;
      // MemberIn body accepts full member payload; we send a status-only patch.
      // Backend keeps everything else since patch merges into member doc.
      await http.patch(`/family-trees/${treeId}/members/${memberId}`, {
        ...m,
        current_status: next,
      });
      toast.success(next === "deceased" ? "Marked as deceased" : "Marked as living");
      // Refresh member data in place
      const { data: p } = await http.get(`/family-trees/${treeId}/members/${memberId}/profile`);
      setData(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally { setSavingStatus(false); }
  };
  const anchors = [
    ["overview", "Overview"],
    has(stats) && ["stats", "Statistics"],
    has(tl) && ["timeline", "Timeline"],
    has(m.spouses) && ["spouses", "Spouses"],
    has(m.children_extra) && ["children", "Children"],
    has(m.occupations) && ["occupations", "Career"],
    has(m.education) && ["education", "Education"],
    has(m.residences) && ["residences", "Residence History"],
    has(m.military) && ["military", "Military"],
    has(m.religion) && ["religion", "Religion"],
    has(m.languages) && ["languages", "Languages"],
    has(m.interests) && ["interests", "Interests"],
    has(m.awards) && ["awards", "Awards"],
    has(m.community_service) && ["community", "Community"],
    has(m.photo_albums) && ["photos", "Photos"],
    has(m.videos) && ["videos", "Videos"],
    has(m.audio) && ["audio", "Audio"],
    has(m.documents) && ["documents", "Documents"],
    (m.memorial_slug || has(m.memorial)) && ["memorial", "Memorial"],
    has(m.stories) && ["stories", "Stories"],
    has(m.genealogy) && ["genealogy", "Genealogy"],
    has(m.bio) && ["bio", "Biography"],
    has(m.contact) && !deceased && ["contact", "Contact"],
    ["community", deceased ? "Memories & Memorial" : "Comments & Updates"],
  ].filter(Boolean);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8" data-testid="member-profile-page">
      <Link to={`/family/${treeSlug}`} className="inline-flex items-center gap-2 text-sm hover:underline mb-4" style={{ color: "var(--wofy-primary)" }}>
        <ChevronLeft className="w-4 h-4" /> Back to {data.tree?.name || "family tree"}
      </Link>

      {/* Cover + portrait hero */}
      <div className="rounded-3xl overflow-hidden border relative mb-6" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="h-40 md:h-56 w-full" style={{
          background: m.cover_photo ? `url(${m.cover_photo}) center/cover` :
            "linear-gradient(135deg, #E9D5FF 0%, #FBCFE8 50%, #FEF3C7 100%)",
        }} />
        <div className="p-6 md:px-10 md:pb-8 flex flex-wrap items-end gap-6 -mt-16 md:-mt-20">
          <div
            className="w-28 h-28 md:w-40 md:h-40 rounded-full overflow-hidden border-4 shrink-0 relative"
            style={{ borderColor: "var(--wofy-paper)", background: "var(--wofy-surface)" }}
          >
            <AncestorAvatar member={m} size={160} className="!rounded-full w-full h-full" />
            <CopyLinkIcon
              url={m.memorial_slug ? `/celebrate/${m.memorial_slug}` : `/family/${treeSlug}/member/${memberId}`}
              position="br"
              label={m.memorial_slug ? "Copy memorial link" : "Copy profile link"}
              testId="member-hero-copy-link"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1" style={{ color: deceased ? "#5B4A73" : "var(--wofy-primary)" }}>
              <TreeDeciduous size={12} className="inline mr-1 -mt-0.5" /> {displayed.toUpperCase()}
              {isUnknown && data.editable && (
                <span className="ml-3 inline-flex gap-1.5 align-baseline" data-testid="member-status-quick-mark">
                  <button
                    disabled={savingStatus}
                    onClick={() => updateStatus("living")}
                    data-testid="member-status-mark-living"
                    className="text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-wider hover:bg-emerald-50"
                    style={{ borderColor: "#A7F3D0", color: "#065F46" }}
                  >
                    ● Mark living
                  </button>
                  <button
                    disabled={savingStatus}
                    onClick={() => updateStatus("deceased")}
                    data-testid="member-status-mark-deceased"
                    className="text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-wider hover:bg-purple-50"
                    style={{ borderColor: "#DDD6FE", color: "#5B4A73" }}
                  >
                    ✦ Mark deceased
                  </button>
                </span>
              )}
            </div>
            <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="member-name">{name || "Unnamed member"}</h1>
            {(m.nickname || m.maiden_name || m.other_names) && (
              <div className="mt-1 text-sm opacity-70">
                {m.nickname && <span className="italic">"{m.nickname}"</span>}
                {m.maiden_name && <span> · née {m.maiden_name}</span>}
                {m.other_names && <span> · also known as {typeof m.other_names === "string" ? m.other_names : (m.other_names[m.other_names.__base || "en"] || "")}</span>}
              </div>
            )}
            <div className="mt-2 text-sm opacity-80 font-mono">
              {yr(m.birth_date) && <span>b. {yr(m.birth_date)}</span>}
              {m.birth_place && <span> · {m.birth_place}</span>}
              {yr(m.death_date) && <span> · d. {yr(m.death_date)}</span>}
              {m.death_place && <span> · {m.death_place}</span>}
              {stats.age != null && <span> · {stats.age} yrs</span>}
            </div>
            {(m.birth_star || (m.alternate_stars || []).length > 0) && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="member-star-signs">
                {m.birth_star && (
                  <span className="text-[11px] rounded-full px-2.5 py-0.5" style={{ background: "#FDF4FF", color: "#86198F", border: "1px solid #F5D0FE" }}>
                    ★ {m.birth_star}
                  </span>
                )}
                {(m.alternate_stars || []).map((s, i) => (s?.name || s?.note) ? (
                  <span key={i} className="text-[11px] rounded-full px-2.5 py-0.5" style={{ background: "#EFF6FF", color: "#1E40AF", border: "1px solid #BFDBFE" }}>
                    {s.system ? <b className="opacity-70 mr-1">{s.system}:</b> : null}{s.name || s.note}
                  </span>
                ) : null)}
              </div>
            )}
          </div>
          {data.editable && (
            <Link
              to={`/family/${treeSlug}/member/${memberId}/edit`}
              className="wofy-btn-primary text-sm"
              data-testid="member-edit-btn"
            >
              <Pencil className="w-4 h-4" /> Edit profile
            </Link>
          )}
          {!deceased && (
            <button
              onClick={() => setShowContact(true)}
              className="wofy-btn-ghost text-sm"
              data-testid="member-contact-btn"
              title="Send a private message to the family admins"
            >
              <MessageSquare className="w-4 h-4" /> Contact
            </button>
          )}
          <button
            onClick={() => setShowSuggestFix(true)}
            className="wofy-btn-ghost text-sm"
            data-testid="member-suggest-fix-btn"
            title="Propose a correction — family managers review before applying"
          >
            <Pencil className="w-4 h-4" /> Suggest a Fix
          </button>
          {deceased && m.memorial_slug && (
            <Link
              to={`/celebrate/${m.memorial_slug}/print`}
              className="wofy-btn-ghost text-sm"
              data-testid="member-print-memorial-btn"
              title="Open a printable memorial page — perfect for funeral programmes"
            >
              <FileText className="w-4 h-4" /> Print Memorial
            </Link>
          )}
          {deceased && (
            <MemorialPageButton treeId={treeSlug} memberId={memberId} testId="member-memorial-page-btn" />
          )}
        </div>
      </div>

      {/* Anchors bar */}
      <nav
        className="sticky top-0 z-20 -mx-6 lg:-mx-10 px-6 lg:px-10 py-2 mb-4 border-b"
        style={{
          background: "rgba(255, 253, 249, 0.94)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderColor: "#E8DFD1",
        }}
      >
        <div className="flex gap-2 overflow-x-auto text-xs font-mono uppercase tracking-wider" data-testid="member-anchors">
          {anchors.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="whitespace-nowrap px-3 py-1.5 rounded-full hover:bg-black/5" style={{ color: "var(--wofy-text-soft)" }}>{label}</a>
          ))}
        </div>
      </nav>

      {/* Completeness */}
      {typeof comp.score === "number" && data.editable && (
        <div className="wofy-card !p-4 mb-6" data-testid="member-completeness">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm">Profile completeness</div>
            <div className="font-mono text-sm">{comp.score}%</div>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "var(--wofy-surface)" }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${comp.score}%`, background: "linear-gradient(90deg, #10B981, #34D399, #A7F3D0)" }} />
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5 text-[10px]">
            {Object.entries(comp.categories || {}).map(([k, filled]) => (
              <span key={k} className={`px-2 py-1 rounded-full ${filled ? "opacity-100" : "opacity-40"}`} style={{ background: filled ? "#D1FAE5" : "var(--wofy-surface)", color: filled ? "#065F46" : "inherit" }}>
                {filled ? "✓" : "○"} {k.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Bio */}
      {has(m.bio) && (
        <Section id="bio" title="Biography" icon={BookOpen}>
          <TranslatableText text={m.bio} testid="member-bio" />
        </Section>
      )}

      {/* Stats */}
      {has(stats) && (
        <Section id="stats" title="Statistics" icon={Sparkles}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox v={stats.age} label="Age" />
            <StatBox v={stats.children_total} label="Children" sub={`${stats.sons || 0} sons · ${stats.daughters || 0} daughters`} />
            <StatBox v={stats.siblings_total} label="Siblings" sub={`${stats.brothers || 0} brothers · ${stats.sisters || 0} sisters`} />
            <StatBox v={stats.grandchildren} label="Grandchildren" sub={stats.great_grandchildren ? `+ ${stats.great_grandchildren} great` : ""} />
            <StatBox v={stats.marriages} label="Marriages" />
            <StatBox v={stats.years_married_max} label="Years married" />
            <StatBox v={stats.countries_lived} label="Countries lived" />
            <StatBox v={stats.cities_lived} label="Cities lived" />
          </div>
        </Section>
      )}

      {/* Timeline */}
      {has(tl) && (
        <Section id="timeline" title="Life Timeline" icon={Clock}>
          <ol className="relative border-l-2 pl-6 ml-2 space-y-4" style={{ borderColor: "var(--wofy-border)" }}>
            {tl.map((ev, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[33px] w-4 h-4 rounded-full" style={{ background: "var(--wofy-primary)" }} />
                <div className="font-mono text-xs opacity-70">{ev.year}</div>
                <div className="font-medium">{ev.title}</div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Spouses */}
      {has(m.spouses) && (
        <Section id="spouses" title="Spouses" icon={Heart}>
          <div className="grid gap-3 md:grid-cols-2">
            {m.spouses.map((s, i) => (
              <div key={i} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="font-medium">{s.name || "Spouse"}</div>
                <div className="text-xs opacity-70 mt-1 space-y-0.5">
                  {s.marriage_date && <div>Married {fmtDate(s.marriage_date)}{s.marriage_location ? ` in ${s.marriage_location}` : ""}</div>}
                  {s.anniversary_note && <div className="italic">{s.anniversary_note}</div>}
                  {s.separation_date && <div>Separated {fmtDate(s.separation_date)}</div>}
                  {s.divorce_date && <div>Divorced {fmtDate(s.divorce_date)}</div>}
                  {s.widowed_date && <div>Widowed {fmtDate(s.widowed_date)}</div>}
                  {s.status && <div className="font-mono uppercase text-[10px]">{s.status}</div>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Children */}
      {has(m.children_extra) && (
        <Section id="children" title="Children" icon={Baby}>
          <ul className="grid gap-2 md:grid-cols-2">
            {m.children_extra.map((c, i) => (
              <li key={i} className="rounded-lg border p-3 flex items-center justify-between gap-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div>
                  <div className="font-medium">{c.name || "Child"}</div>
                  <div className="text-xs opacity-70">
                    {yr(c.birth_date) && <span>b. {yr(c.birth_date)}</span>}
                    {c.gender && <span> · {c.gender}</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {c.adopted && <Tag color="#F59E0B">adopted</Tag>}
                  {c.foster && <Tag color="#0EA5E9">foster</Tag>}
                  {c.step && <Tag color="#6366F1">step</Tag>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Occupations */}
      {has(m.occupations) && (
        <Section id="occupations" title="Career" icon={Briefcase}>
          <div className="space-y-3">
            {m.occupations.map((o, i) => (
              <div key={i} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="font-medium">{o.title}{o.company ? ` · ${o.company}` : ""}</div>
                  <div className="font-mono text-xs opacity-70">{o.years_active}{o.retirement_year ? ` · Retired ${o.retirement_year}` : ""}</div>
                </div>
                {o.industry && <div className="text-xs opacity-70">{o.industry}</div>}
                {o.notes && <p className="text-sm mt-1.5 opacity-80">{o.notes}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Education */}
      {has(m.education) && (
        <Section id="education" title="Education" icon={GraduationCap}>
          <div className="space-y-3">
            {m.education.map((e, i) => (
              <div key={i} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="font-medium">{e.name || e.degree || "Education"}</div>
                  <div className="font-mono text-xs opacity-70">{e.graduation_year || ""}</div>
                </div>
                <div className="text-xs opacity-70 flex flex-wrap gap-2 mt-0.5">
                  {e.level && <span className="font-mono uppercase">{e.level}</span>}
                  {e.area && <span>· {e.area}</span>}
                  {e.degree && e.name && <span>· {e.degree}</span>}
                </div>
                {e.notes && <p className="text-sm mt-1.5 opacity-80">{e.notes}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Residences */}
      {has(m.residences) && (
        <Section id="residences" title="Residence History" icon={MapPin}>
          <ol className="space-y-2">
            {m.residences.map((r, i) => (
              <li key={i} className="flex items-baseline gap-2 text-sm">
                <span className="font-mono text-xs opacity-70 min-w-[110px]">
                  {yr(r.from_date)}{r.from_date && r.to_date ? " – " : ""}{yr(r.to_date) || (r.from_date && !r.to_date ? "present" : "")}
                </span>
                <span>{[r.city, r.state, r.country].filter(Boolean).join(", ")}</span>
                {r.kind && <span className="text-[10px] font-mono uppercase opacity-60">· {r.kind}</span>}
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Military */}
      {has(m.military) && (
        <Section id="military" title="Military Service" icon={Flag}>
          <KeyVals kv={{
            "Branch": m.military.branch, "Rank": m.military.rank,
            "Service number": m.military.service_number, "Years served": m.military.years_served,
            "Wars": (m.military.wars || []).join(", "),
            "Medals": (m.military.medals || []).join(", "),
            "Veteran status": m.military.veteran_status,
          }} />
          {m.military.notes && <p className="text-sm mt-2 opacity-80">{m.military.notes}</p>}
        </Section>
      )}

      {/* Religion */}
      {has(m.religion) && (
        <Section id="religion" title="Religion" icon={Church}>
          <KeyVals kv={{
            "Faith": m.religion.faith, "Parish": m.religion.parish,
            "Place of worship": m.religion.place_of_worship,
            "Baptism": fmtDate(m.religion.baptism_date),
            "Confirmation": fmtDate(m.religion.confirmation_date),
            "Ordination": fmtDate(m.religion.ordination_date),
          }} />
          {m.religion.notes && <p className="text-sm mt-2 opacity-80">{m.religion.notes}</p>}
        </Section>
      )}

      {/* Languages */}
      {has(m.languages) && (
        <Section id="languages" title="Languages" icon={LangIcon}>
          {m.languages.native?.length > 0 && <div className="text-sm"><b>Native:</b> {m.languages.native.join(", ")}</div>}
          {m.languages.other?.length > 0 && <div className="text-sm mt-1"><b>Other:</b> {m.languages.other.join(", ")}</div>}
        </Section>
      )}

      {/* Interests */}
      {has(m.interests) && (
        <Section id="interests" title="Personal Interests" icon={Heart}>
          <div className="flex flex-wrap gap-2">
            {Object.entries(m.interests).flatMap(([k, arr]) =>
              (Array.isArray(arr) ? arr : (arr ? [arr] : [])).map((v, i) => (
                <span key={`${k}-${i}`} className="text-xs rounded-full px-2.5 py-1" style={{ background: "var(--wofy-surface)" }}>
                  <b className="opacity-60">{k}:</b> {v}
                </span>
              ))
            )}
          </div>
        </Section>
      )}

      {/* Awards */}
      {has(m.awards) && (
        <Section id="awards" title="Awards & Achievements" icon={Trophy}>
          <div className="space-y-2">
            {m.awards.map((a, i) => (
              <div key={i} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="font-medium">{a.award}</div>
                  <div className="font-mono text-xs opacity-70">{fmtDate(a.date)}</div>
                </div>
                {a.organization && <div className="text-xs opacity-70">{a.organization}</div>}
                {a.description && <p className="text-sm mt-1.5 opacity-80">{a.description}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Community Service */}
      {has(m.community_service) && (
        <Section id="community" title="Community Service" icon={HandHeart}>
          <div className="space-y-2">
            {m.community_service.map((c, i) => (
              <div key={i} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="font-medium">{c.role || c.type}</div>
                <div className="text-xs opacity-70">{c.org} · {yr(c.from_date)}{c.to_date ? ` – ${yr(c.to_date)}` : ""}</div>
                {c.description && <p className="text-sm mt-1.5 opacity-80">{c.description}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Photo albums */}
      {has(m.photo_albums) && (
        <Section id="photos" title="Photos" icon={Images}>
          {m.photo_albums.map((alb, ai) => (
            <div key={ai} className="mb-6">
              <div className="text-sm font-mono uppercase tracking-wider mb-2 opacity-70">{alb.name}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {(alb.photos || []).map((p, i) => (
                  <a key={i} href={p} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden">
                    <img src={p} alt="" className="w-full h-32 object-cover hover:opacity-90 transition-opacity" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Videos */}
      {has(m.videos) && (
        <Section id="videos" title="Videos" icon={Video}>
          <div className="grid gap-3 md:grid-cols-2">
            {m.videos.map((v, i) => (
              <a key={i} href={v.url} target="_blank" rel="noreferrer" className="rounded-lg border overflow-hidden hover:opacity-90" style={{ borderColor: "var(--wofy-border)" }}>
                {v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-40 object-cover" loading="lazy" />}
                <div className="p-3">
                  <div className="font-medium">{v.title}</div>
                  {v.kind && <div className="text-xs opacity-60 uppercase font-mono">{v.kind}</div>}
                  {v.note && <p className="text-sm opacity-80 mt-1">{v.note}</p>}
                </div>
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* Audio */}
      {has(m.audio) && (
        <Section id="audio" title="Audio Recordings" icon={AudioLines}>
          <ul className="space-y-2">
            {m.audio.map((a, i) => (
              <li key={i} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <b>{a.title}</b>
                  {a.kind && <span className="text-[10px] font-mono uppercase opacity-60">{a.kind}</span>}
                </div>
                {a.url && <audio controls preload="none" src={a.url} className="w-full mt-2" />}
                {a.note && <p className="text-sm opacity-80 mt-1">{a.note}</p>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Documents */}
      {has(m.documents) && (
        <Section id="documents" title="Documents" icon={FileText}>
          <ul className="grid gap-2 md:grid-cols-2">
            {m.documents.map((d, i) => (
              <li key={i} className="flex items-baseline gap-3 rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <FileText className="w-4 h-4 opacity-60 shrink-0" />
                <div className="flex-1 min-w-0">
                  <a href={d.url} target="_blank" rel="noreferrer" className="font-medium hover:underline break-words">{d.name || d.kind || "Document"}</a>
                  <div className="text-[10px] font-mono uppercase opacity-60">{d.kind}</div>
                  {d.note && <p className="text-xs opacity-70 mt-1">{d.note}</p>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Memorial */}
      {(m.memorial_slug || has(m.memorial)) && (
        <Section id="memorial" title="Memorial" icon={Flower2}>
          {m.memorial_slug && (
            <Link
              to={`/celebrate/${m.memorial_slug}`}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium mb-3"
              style={{ background: "#5B4A73", color: "white" }}
              data-testid="member-memorial-link"
            >
              <Flower2 className="w-4 h-4" /> Visit Celebrate a Life memorial
            </Link>
          )}
          {has(m.memorial) && (
            <>
              <KeyVals kv={{
                "Cemetery": m.memorial.cemetery, "Gravesite": m.memorial.gravesite,
                "GPS": m.memorial.gps_lat && m.memorial.gps_lng ? `${m.memorial.gps_lat}, ${m.memorial.gps_lng}` : "",
              }} />
              {m.memorial.obituary && (
                <div className="mt-3">
                  <div className="font-mono text-xs uppercase opacity-60 mb-1">Obituary</div>
                  <TranslatableText text={m.memorial.obituary} testid="member-memorial-obituary" />
                </div>
              )}
              {(m.memorial.eulogies || []).map((eul, i) => (
                <div key={i} className="mt-3">
                  <div className="font-mono text-xs uppercase opacity-60 mb-1">Eulogy</div>
                  <TranslatableText text={eul} testid={`member-memorial-eulogy-${i}`} />
                </div>
              ))}
            </>
          )}
        </Section>
      )}

      {/* Stories */}
      {(has(m.stories) || user) && (
        <Section id="stories" title="Family Stories" icon={BookOpen}>
          {user && (
            <div className="grid gap-3 md:grid-cols-2 mb-4" data-testid="member-contribute-boxes">
              <div className="rounded-xl border p-4" style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #FEF3C7 0%, #FFFFFF 60%)" }}>
                <div className="overline mb-1" style={{ color: "#92400E" }}>SHARE A STORY</div>
                <div className="text-sm mb-3" style={{ color: "var(--wofy-text-soft)" }}>
                  A memory, tradition, quote or lesson from this person.
                </div>
                <ShareStoryButton treeId={data.tree?.id} memberId={memberId} onCreated={() => window.location.reload()} />
              </div>
              <div className="rounded-xl border p-4" style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #EDE9FE 0%, #FFFFFF 60%)" }}>
                <div className="overline mb-1" style={{ color: "#4338CA" }}>CONTRIBUTE TO THIS MEMBER</div>
                <div className="text-sm mb-3" style={{ color: "var(--wofy-text-soft)" }}>
                  Photos, videos, documents or a note to the admin — indexed under this person.
                </div>
                <ContributeToMemberActions treeId={data.tree?.id} memberId={memberId} memberName={m.first_name} />
              </div>
            </div>
          )}
          <div className="space-y-4 mt-3">
            {(m.stories || []).filter((s) => (s.status || "approved") === "approved").map((s, i) => (
              <div key={s.id || i} className="rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                  <b>{s.title || "Story"}</b>
                  {s.kind && <span className="text-[10px] font-mono uppercase opacity-60">{s.kind}</span>}
                </div>
                <TranslatableText text={s.body} testid={`member-story-${i}`} />
                {(s.contributor_name || s.created_at) && (
                  <div className="text-xs opacity-60 mt-2">— {s.contributor_name || "Anonymous"}{s.created_at ? ` · ${fmtDate(s.created_at)}` : ""}</div>
                )}
              </div>
            ))}
            {data.editable && (m.stories || []).some((s) => s.status === "pending") && (
              <div className="rounded-lg border-2 border-dashed p-3 text-xs" style={{ borderColor: "#F59E0B", background: "#FFFBEB", color: "#92400E" }}>
                {(m.stories || []).filter((s) => s.status === "pending").length} story{(m.stories || []).filter((s) => s.status === "pending").length === 1 ? "" : "s"} pending your review — approve them in the family tree Access tab.
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Genealogy */}
      {has(m.genealogy) && (
        <Section id="genealogy" title="Genealogy" icon={TreeDeciduous}>
          <KeyVals kv={{
            "Branch": m.genealogy.branch, "Clan": m.genealogy.clan,
            "House name": m.genealogy.house_name,
            "Surname origin": m.genealogy.surname_origin,
            "Ethnicity": m.genealogy.ethnicity,
            "Family motto": m.genealogy.family_motto,
          }} />
          {m.genealogy.coat_of_arms_url && (
            <img src={m.genealogy.coat_of_arms_url} alt="Coat of arms" className="mt-3 max-h-32" />
          )}
          {(m.genealogy.dna_test_links || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {m.genealogy.dna_test_links.map((l, i) => (
                <a key={i} href={l} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: "var(--wofy-primary)" }}>DNA test #{i + 1}</a>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Contact */}
      {!deceased && has(m.contact) && (
        <Section id="contact" title="Contact" icon={Phone}>
          <KeyVals kv={{
            "Current city": [m.contact.current_city, m.contact.state, m.contact.country].filter(Boolean).join(", "),
            "Phone": m.contact.phone, "Email": m.contact.email, "Website": m.contact.website,
          }} />
          {m.contact.social && Object.keys(m.contact.social).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(m.contact.social).map(([k, v]) => v && (
                <a key={k} href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer"
                   className="text-xs rounded-full px-3 py-1 border hover:opacity-80"
                   style={{ borderColor: "var(--wofy-border)" }}>
                  {k}
                </a>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Family Archive items tagged with this person */}
      <ArchiveFromProfile memberId={memberId} treeSlug={treeSlug} />

      {/* Wishes & greetings saved to this member's life timeline */}
      <div className="mt-6">
        <div className="flex justify-end mb-2">
          <Link
            to={`/family/${treeSlug}/wishes?member=${memberId}`}
            className="wofy-btn-primary text-xs"
            data-testid="member-send-wish-btn"
          >
            🎉 Send a wish — occasions &amp; remembrances
          </Link>
        </div>
        <WishesWall treeId={data.tree?.id} memberId={memberId} canModerate={data.editable} meId={user?.id} />
      </div>

      {/* Voice Memories — works for living and memorial profiles alike */}
      <div className="mt-6">
        <VoiceMemories endpoint={`/persons/${memberId}/voice-memories`} personName={m.first_name || "them"} user={user} />
      </div>

      {/* Profile Interaction & Memorial Community (Phase 1) */}
      <div className="mt-6">
        <ProfileCommunity personId={memberId} />
      </div>

      {/* Facebook-style discussion forum (2026-08-22 — per-member
          message board with threaded replies, emoji reactions, memory
          tags and location tags). Rendered under the community strip
          so existing ProfileCommunity tributes still show up first. */}
      <div className="mt-6">
        <MemberForum
          memberId={memberId}
          canWrite={!!user}
          meUid={user?.firebase_uid || user?.id}
        />
      </div>

      {showContact && (
        <ContactMemberModal
          treeId={data.tree?.id || treeSlug}
          memberId={memberId}
          memberName={name}
          onClose={() => setShowContact(false)}
        />
      )}
      {showSuggestFix && (
        <SuggestFixModal
          personId={memberId}
          memberName={name}
          currentValues={{
            first_name: m.first_name,
            middle_name: m.middle_name,
            last_name: m.last_name,
            nickname: m.nickname,
            maiden_name: m.maiden_name,
            birth_date: m.birth_date,
            birth_place: m.birth_place,
            death_date: m.death_date,
            death_place: m.death_place,
            current_status: m.current_status,
            gender: m.gender,
            bio: m.bio,
          }}
          onClose={() => setShowSuggestFix(false)}
        />
      )}
    </div>
  );
}

// ---------- tiny presentational components ----------

function Section({ id, title, icon: Icon, children }) {
  return (
    <section id={id} className="wofy-card !p-5 mb-4 scroll-mt-24" data-testid={`member-section-${id}`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />}
        <h2 className="font-display text-xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function StatBox({ v, label, sub }) {
  const shown = v == null || v === 0 ? null : v;
  if (shown == null) return null;
  return (
    <div className="rounded-xl border p-3 text-center" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="font-display text-2xl">{v}</div>
      <div className="text-xs opacity-70">{label}</div>
      {sub && <div className="text-[10px] font-mono opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function KeyVals({ kv }) {
  const rows = Object.entries(kv).filter(([, v]) => v != null && String(v).length > 0);
  if (rows.length === 0) return null;
  return (
    <dl className="grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <dt className="opacity-60 min-w-[110px]">{k}</dt>
          <dd className="flex-1 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Tag({ color, children }) {
  return <span className="text-[10px] font-mono uppercase rounded-full px-2 py-0.5" style={{ background: `${color}20`, color }}>{children}</span>;
}

function ShareStoryButton({ treeId, memberId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("childhood");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 10) return toast.error("Share at least a couple of sentences.");
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/members/${memberId}/stories`, { title: title.trim() || undefined, kind, body: body.trim() });
      if (data?.story?.status === "pending") {
        toast.success("Story shared — pending owner approval before it appears.");
      } else {
        toast.success("Story published.");
      }
      setTitle(""); setBody(""); setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't share your story.");
    } finally { setBusy(false); }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="wofy-btn-ghost text-xs"
        data-testid="member-share-story-btn"
      >
        <BookOpen className="w-3.5 h-3.5" /> Share a story
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="wofy-card w-full max-w-lg text-left" onClick={(e) => e.stopPropagation()} data-testid="share-story-modal">
            <div className="overline mb-2">SHARE A FAMILY STORY</div>
            <h3 className="font-display text-xl mb-1">A memory that shouldn't be lost</h3>
            <p className="text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>
              If you're not an editor for this member, your story will be reviewed by the tree owner before appearing.
            </p>
            <input dir="auto" className="wofy-input mb-2" placeholder="Story title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="share-story-title" />
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="wofy-input mb-2" data-testid="share-story-kind">
              <option value="childhood">Childhood story</option>
              <option value="funny">Funny story</option>
              <option value="lesson">Life lesson</option>
              <option value="tradition">Family tradition</option>
              <option value="recipe">Recipe</option>
              <option value="quote">Quote</option>
              <option value="expression">Favorite expression</option>
              <option value="other">Other</option>
            </select>
            <textarea dir="auto" className="wofy-input" placeholder="Share what you remember — no detail is too small." value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={8000} data-testid="share-story-body" />
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={() => setOpen(false)} className="wofy-btn-ghost text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="share-story-submit">{busy ? "Sharing…" : "Share story"}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}


/* ------------------------------------------------------------------
 * ContributeToMemberActions — 4 action buttons that each open a
 * small modal. All contributions are POSTed to
 * /api/family-trees/{tree}/contributions with a `member_id` so they
 * appear tagged in the Family Photos tab of the Family Management
 * Centre.
 * ---------------------------------------------------------------- */
function ContributeToMemberActions({ treeId, memberId, memberName }) {
  const [modal, setModal] = useState(null); // "photo" | "video" | "document" | "message" | null
  const close = () => setModal(null);
  const done = () => {
    toast.success("Thanks! Your contribution has been added.");
    setModal(null);
  };
  const Btn = ({ id, icon: Icon, label, kind, color }) => (
    <button
      type="button"
      onClick={() => setModal(kind)}
      className="w-full text-left rounded-lg border px-3 py-2 flex items-center gap-2 text-sm hover:shadow-sm transition"
      style={{ borderColor: "var(--wofy-border)", background: "#FFFFFF" }}
      data-testid={id}
    >
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
      <span className="font-medium">{label}</span>
    </button>
  );
  return (
    <div className="space-y-2">
      <Btn id="contrib-photo-btn"   icon={Images}       kind="photo"    label="Upload photos"   color="#DB2777" />
      <Btn id="contrib-video-btn"   icon={Video}        kind="video"    label="Submit video links" color="#DC2626" />
      <Btn id="contrib-doc-btn"     icon={FileText}     kind="document" label="Upload a document"  color="#0891B2" />
      <Btn id="contrib-message-btn" icon={MessageSquare} kind="message"  label="Message admin"     color="#7C3AED" />
      {modal && (
        <MemberContributeModal
          kind={modal}
          treeId={treeId}
          memberId={memberId}
          memberName={memberName}
          onClose={close}
          onDone={done}
        />
      )}
    </div>
  );
}

// "From the Family Archive" — files tagged with this person in the tree's
// shared archive. Renders nothing when there are no tagged files.
function ArchiveFromProfile({ memberId, treeSlug }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    http.get(`/persons/${memberId}/archive-files`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, [memberId]);
  if (!items.length) return null;
  return (
    <div className="mt-6 wofy-card" data-testid="profile-archive-section">
      <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>FROM THE FAMILY ARCHIVE ({items.length})</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((f) => (
          <a
            key={f.id} href={resolveImage(f.url)} target="_blank" rel="noreferrer"
            className="block rounded-lg border overflow-hidden hover:shadow-md transition"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`profile-archive-item-${f.id}`}
          >
            {f.kind === "photo" ? (
              <img src={resolveImage(f.url)} alt={f.caption || f.filename} className="w-full h-28 object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-28 flex items-center justify-center" style={{ background: "var(--wofy-surface)" }}>
                <FileText className="w-8 h-8" style={{ color: "var(--wofy-primary)" }} />
              </div>
            )}
            <div className="px-2 py-1.5 text-[11px] truncate">{f.caption || f.filename}</div>
          </a>
        ))}
      </div>
      {treeSlug && (
        <Link to={`/family/${treeSlug}?tab=archive`} className="text-xs underline opacity-70 mt-3 inline-block">
          Open the family archive →
        </Link>
      )}
    </div>
  );
}
